import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../utils/axios';
import parse from 'html-react-parser';
import { 
    Card, 
    CardContent, 
    Typography, 
    Grid, 
    TextField, 
    Box, 
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

// Helper function to strip HTML tags
const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
};

const ArticleList = () => {
    const [articles, setArticles] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchArticles();
    }, [selectedCategory]);

    const fetchArticles = async () => {
        try {
            setLoading(true);
            setError(null);
            const url = selectedCategory === 'all' 
                ? '/api/articles/published'
                : `/api/articles/category/${selectedCategory}`;
            const response = await axios.get(url);
            setArticles(response.data);
            
            // Extract unique categories from articles
            const uniqueCategories = [...new Set(response.data.map(article => article.category))];
            setCategories(uniqueCategories);
        } catch (err) {
            setError(err.response?.data?.message || 'Error fetching articles');
            console.error('Error fetching articles:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredArticles = articles.filter(article => {
        const plainTextContent = stripHtml(article.content);
        return article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            plainTextContent.toLowerCase().includes(searchTerm.toLowerCase());
    });

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        label="All"
                        onClick={() => setSelectedCategory('all')}
                        color={selectedCategory === 'all' ? 'primary' : 'default'}
                    />
                    {categories.map((category) => (
                        <Chip
                            key={category}
                            label={category}
                            onClick={() => setSelectedCategory(category)}
                            color={selectedCategory === category ? 'primary' : 'default'}
                        />
                    ))}
                </Box>
            </Box>

            <Grid container spacing={3}>
                {filteredArticles.map((article) => (
                    <Grid item xs={12} sm={6} md={4} key={article._id}>
                        <Card 
                            component={Link} 
                            to={`/articles/${article._id}`}
                            elevation={1}
                            sx={{ 
                                height: '100%',
                                textDecoration: 'none',
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                    boxShadow: '0 6px 12px rgba(0,0,0,0.1)'
                                }
                            }}
                        >
                            <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    {article.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    {new Date(article.createdAt).toLocaleDateString()}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {stripHtml(article.content).substring(0, 150)}...
                                </Typography>
                                <Chip 
                                    label={article.category} 
                                    size="small" 
                                    sx={{ mt: 2 }}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default ArticleList; 